import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import Modal from "react-modal";
import { AlertTriangle } from "lucide-react";

/**
 * The panel's own "are you sure?".
 *
 * `window.confirm` is drawn by the browser: a white Chrome sheet with the
 * origin printed on it, pinned to the top of the screen, in a panel that is
 * otherwise black. It also can't show the thing being decided about — the
 * comment's text, the post's title — as anything but more plain prose.
 *
 * Usage is a drop-in for the call it replaces, because it returns a promise:
 *
 *   const confirm = useConfirm();
 *   if (!(await confirm({ title: "Delete this comment?" }))) return;
 *
 * Options: `title` (required), `body` (a node or string), `quote` (the text
 * being acted on, rendered as a quote), `confirmLabel`, `cancelLabel`, and
 * `tone` — "danger" (default, red), "primary" (green) or "neutral".
 * `cancelLabel: null` drops the Cancel button, for a notice with one answer.
 */

const ConfirmContext = createContext(null);

const TONE_CLASS = {
  danger: "btn-danger",
  primary: "btn-dark",
  neutral: "btn-outline-secondary",
};

export const ConfirmProvider = ({ children }) => {
  const [request, setRequest] = useState(null);
  // The promise is settled from whichever button is pressed, so the resolver
  // has to outlive the render that created it.
  const resolverRef = useRef(null);

  const settle = useCallback((answer) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setRequest(null);
    if (resolve) resolve(answer);
  }, []);

  const confirm = useCallback((options) => {
    // A second ask while one is open answers the first "no" rather than
    // leaving its caller waiting on a promise nothing can settle.
    if (resolverRef.current) resolverRef.current(false);
    setRequest(typeof options === "string" ? { title: options } : options || {});
    return new Promise((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const value = useMemo(() => confirm, [confirm]);

  const tone = request?.tone || "danger";

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      {request && (
        <Modal
          isOpen
          onRequestClose={() => settle(false)}
          className="modal_body_sm"
          overlayClassName="modal_overlay"
          ariaHideApp={false}
        >
          {/* `modal_body_sm` is a white panel from the light theme;
              `box_wrapper` paints it in the panel's own surface colour. */}
          <div className="box_wrapper">
            <div className="d-flex gap-3">
              <AlertTriangle
                size={22}
                className={tone === "danger" ? "text-danger" : "text-warning"}
                style={{ flexShrink: 0, marginTop: 2 }}
              />
              <div className="flex-grow-1">
                <h5 className="mb-2">{request.title}</h5>
                {request.body ? (
                  <div className="text-muted small mb-0">{request.body}</div>
                ) : null}
                {request.quote ? (
                  <blockquote
                    className="small mb-0 mt-3"
                    style={{
                      borderLeft: "2px solid var(--ps-border)",
                      paddingLeft: 12,
                      maxHeight: 160,
                      overflowY: "auto",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {request.quote}
                  </blockquote>
                ) : null}
              </div>
            </div>

            <div className="d-flex justify-content-end gap-2 mt-4">
              {request.cancelLabel !== null && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => settle(false)}
                >
                  {request.cancelLabel || "Cancel"}
                </button>
              )}
              <button
                type="button"
                className={`btn btn-sm ${TONE_CLASS[tone] || TONE_CLASS.danger}`}
                autoFocus
                onClick={() => settle(true)}
              >
                {request.confirmLabel || "Confirm"}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </ConfirmContext.Provider>
  );
};

/**
 * Returns `confirm(options) => Promise<boolean>`. Outside the provider it
 * falls back to `window.confirm` so a stray caller still asks SOMETHING
 * rather than silently going ahead.
 */
export const useConfirm = () => {
  const confirm = useContext(ConfirmContext);
  return useMemo(
    () =>
      confirm ||
      ((options) => {
        const o = typeof options === "string" ? { title: options } : options || {};
        return Promise.resolve(window.confirm(o.title || "Are you sure?"));
      }),
    [confirm]
  );
};

export default ConfirmProvider;

/**
 * The API refuses a create whose thing is already there with 409 and
 * `code: "DUPLICATE"` — an author, book, summary, recommender, series,
 * journey, genre, profession, tag or pillar of the same name.
 */
export const isDuplicateError = (error) =>
  error?.response?.status === 409 && error.response.data?.code === "DUPLICATE";

/**
 * Returns `(error) => boolean`: shows the "Already exists" popup and answers
 * true when `error` is that refusal, so an add form's catch becomes
 *
 *   if (!showAlreadyExists(error)) toast.error(message);
 *
 * A popup rather than the failure toast: nothing broke, the admin needs to
 * stop and pick something different, and a toast is gone before it's read.
 */
export const useAlreadyExists = () => {
  const confirm = useConfirm();
  return useCallback(
    (error) => {
      if (!isDuplicateError(error)) return false;
      confirm({
        title: "Already exists",
        body: (
          <>
            {error.response.data.message}
            <br />
            There's no need to add it again — try adding something different.
          </>
        ),
        confirmLabel: "OK",
        cancelLabel: null,
        tone: "primary",
      });
      return true;
    },
    [confirm]
  );
};
